import React, { useState, useEffect } from 'react';
import { ptzApi, presetApi } from '../../services/api';
import { useDriverFor } from '../../hooks/useCameraDrivers';
import { useAppStore } from '../../store/appStore';
import SyncPresetsModal from './SyncPresetsModal';

export default function PresetGrid({ camera, activePresetId, onPresetActive }) {
  const driver = useDriverFor(camera);
  const { addToast, user } = useAppStore();
  const canManage = user?.role !== 'customer_operator' && user?.role !== 'volunteer_operator';

  const [presets, setPresets] = useState([]);
  const [newPresetName, setNewPresetName] = useState('');
  const [savingPreset, setSavingPreset] = useState(false);
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [syncingAll, setSyncingAll] = useState(false);
  const [capturingThumb, setCapturingThumb] = useState(null);
  const [updatingPos, setUpdatingPos] = useState(null);
  const [expandedPresetId, setExpandedPresetId] = useState(null);
  const [editingPresetId, setEditingPresetId] = useState(null);
  const [editingName, setEditingName] = useState('');

  useEffect(() => {
    loadPresets();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [camera.id]);

  async function loadPresets() {
    try {
      const res = await presetApi.list(camera.id);
      setPresets(res.data);
    } catch { setPresets([]); }
  }

  async function gotoPreset(preset) {
    try {
      // Locally-stored presets carry an absolute pan/tilt/zoom; camera-side presets
      // are recalled by id. Works the same for every manufacturer via the resolved API.
      // converge: recalling a preset is usually a long move, and nothing follows it to
      // correct an arrival that falls short.
      //
      // Feedback goes out before the request, not after: a converging recall only resolves
      // once the camera has actually arrived, which is seconds, and a preset button that
      // sits dead until then reads as a missed click.
      const name = preset.displayName || preset.name;
      onPresetActive?.({ id: preset.id, name });
      addToast({ type: 'info', message: `Moving to: ${name}` });

      if (preset.pan != null && preset.tilt != null && preset.zoom != null) {
        await ptzApi.ptzAbsolute(camera.id, { pan: preset.pan, tilt: preset.tilt, zoom: preset.zoom, converge: true });
      } else {
        await ptzApi.gotoPreset(camera.id, preset.cameraPresetId ?? preset.id);
      }
    } catch {
      addToast({ type: 'error', message: 'Failed to go to preset' });
    }
  }

  async function saveNewPreset() {
    if (!newPresetName) return;
    const name = newPresetName;
    setSavingPreset(true);
    try {
      await ptzApi.savePreset(camera.id, name);
      setNewPresetName('');
      await loadPresets();
      addToast({ type: 'success', message: `Preset "${name}" saved` });
    } catch (err) {
      addToast({ type: 'error', message: err.response?.data?.error || 'Failed to save preset' });
    } finally { setSavingPreset(false); }
  }

  async function syncAllPresets() {
    setSyncingAll(true);
    try {
      const res = await ptzApi.ptzPresets(camera.id);
      if (res.data.length === 0) {
        addToast({ type: 'warning', message: 'No presets found on camera' });
        setSyncingAll(false);
        return;
      }
      const localPresets = presets.filter(p => p.source === 'axisvms');
      const cameraPresets = res.data.map(p => ({ id: p.id, name: p.name, displayName: p.name, source: 'camera', cameraPresetId: p.id }));
      await presetApi.save(camera.id, [...cameraPresets, ...localPresets]);
      await loadPresets();
      addToast({ type: 'success', message: `Synced ${res.data.length} camera preset(s)` });
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Failed to sync presets';
      addToast({ type: 'error', message: `Sync failed: ${msg}` });
    } finally { setSyncingAll(false); }
  }

  async function captureThumb(preset) {
    setCapturingThumb(preset.id);
    try {
      const res = await ptzApi.capturePresetThumbnail(camera.id, preset.id);
      setPresets(prev => prev.map(p => p.id === preset.id ? { ...p, thumbnailUrl: res.data.thumbnailUrl } : p));
      setExpandedPresetId(null);
      addToast({ type: 'success', message: `Thumbnail captured for "${preset.displayName || preset.name}"` });
    } catch {
      addToast({ type: 'error', message: 'Failed to capture thumbnail' });
    } finally { setCapturingThumb(null); }
  }

  async function updatePosition(preset) {
    setUpdatingPos(preset.id);
    try {
      const posRes = await ptzApi.ptzPosition(camera.id);
      await presetApi.update(camera.id, preset.id, { pan: posRes.data.pan, tilt: posRes.data.tilt, zoom: posRes.data.zoom });
      // Only some cameras keep presets on the device; for the rest the app stores a position
      // and there is nothing on the camera to reposition. The driver says which.
      if (driver?.presets.cameraSide && preset.source === 'camera') {
        try { await ptzApi.updatePresetPosition(camera.id, preset.cameraPresetId ?? preset.id, preset.displayName || preset.name); } catch {}
      }
      await loadPresets();
      addToast({ type: 'success', message: `Position saved for "${preset.displayName || preset.name}"` });
    } catch (err) {
      addToast({ type: 'error', message: err.response?.data?.error || 'Failed to update position' });
    } finally { setUpdatingPos(null); }
  }

  async function deletePreset(preset) {
    const label = preset.displayName || preset.name;
    if (!window.confirm(`Delete preset "${label}"? This cannot be undone.`)) return;
    try {
      await presetApi.remove(camera.id, preset.id);
      setPresets(prev => prev.filter(p => p.id !== preset.id));
      setExpandedPresetId(null);
      addToast({ type: 'success', message: `Preset "${label}" deleted` });
    } catch (err) {
      addToast({ type: 'error', message: err.response?.data?.error || 'Failed to delete preset' });
    }
  }

  async function saveEditName(preset) {
    if (!editingName.trim()) { setEditingPresetId(null); return; }
    const name = editingName.trim();
    try {
      await presetApi.update(camera.id, preset.id, { displayName: name });
      setPresets(prev => prev.map(p => p.id === preset.id ? { ...p, displayName: name } : p));
    } catch {
      addToast({ type: 'error', message: 'Failed to rename preset' });
    } finally { setEditingPresetId(null); }
  }

  return (
    <div style={{ marginTop: '4px' }}>
      {/* Header — title, save-preset input, and sync actions all on one line */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
        <div className="section-title" style={{ flexShrink: 0 }}>Presets ({presets.length})</div>
        {canManage && (
          <>
            <input
              className="input-field"
              style={{ height: '32px', fontSize: '12px', width: '200px', flexShrink: 0 }}
              placeholder="Save current position..."
              value={newPresetName}
              onChange={e => setNewPresetName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && saveNewPreset()}
            />
            <button className="btn btn-ghost btn-sm" onClick={saveNewPreset} disabled={savingPreset || !newPresetName} style={{ flexShrink: 0 }}>
              {savingPreset ? '...' : 'Save'}
            </button>
            <div style={{ flex: 1 }} />
            <button className="btn btn-ghost btn-sm" onClick={() => setShowSyncModal(true)} title="Choose camera presets" style={{ flexShrink: 0 }}>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 102.13-9.36L1 10"/></svg>
              Sync
            </button>
            <button className="btn btn-ghost btn-sm" onClick={syncAllPresets} disabled={syncingAll} style={{ flexShrink: 0 }}>
              {syncingAll ? 'Syncing...' : 'Sync All'}
            </button>
          </>
        )}
      </div>

      {/* Grid */}
      {presets.length === 0 ? (
        <div style={{ fontFamily: 'var(--font-mono)', fontSize: '11px', color: 'var(--text-muted)', textAlign: 'center', padding: '24px', lineHeight: 1.6, background: 'var(--bg-surface)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-dim)' }}>
          No presets synced.<br />
          Click "Sync" to choose from camera presets, or "Sync All" to import all.
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(185px, 1fr))', gap: '8px' }}>
          {presets.map(preset => {
            const isExpanded = expandedPresetId === preset.id;
            const isEditingName = editingPresetId === preset.id;
            const hasThumbnail = !!preset.thumbnailUrl;
            const isActive = activePresetId === preset.id;
            return (
              <div
                key={preset.id}
                style={{
                  position: 'relative',
                  borderRadius: 'var(--radius-md)',
                  overflow: 'hidden',
                  border: `1px solid ${isExpanded ? 'var(--accent-orange)' : isActive ? 'var(--accent-green)' : 'var(--border-dim)'}`,
                  boxShadow: isActive ? '0 0 8px rgba(0,255,136,0.25)' : 'none',
                  background: 'var(--bg-void)',
                  transition: 'border-color 0.15s, box-shadow 0.15s',
                }}
              >
                {/* 16:9 enforcer */}
                <div style={{ paddingTop: '56.25%', position: 'relative' }}>

                  {/* Thumbnail or placeholder */}
                  {hasThumbnail ? (
                    <img
                      src={preset.thumbnailUrl}
                      alt={preset.name}
                      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                      onError={e => { e.target.style.display = 'none'; }}
                    />
                  ) : (
                    <div style={{ position: 'absolute', inset: 0, background: 'var(--bg-surface)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" opacity="0.2">
                        <path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z"/><circle cx="12" cy="13" r="4"/>
                      </svg>
                    </div>
                  )}

                  {/* Edit toggle top-right */}
                  {canManage && (
                    <button
                      onClick={e => { e.stopPropagation(); setExpandedPresetId(isExpanded ? null : preset.id); }}
                      title={isExpanded ? 'Close edit' : 'Edit preset'}
                      style={{
                        position: 'absolute', top: '5px', right: '5px',
                        width: '22px', height: '22px',
                        background: isExpanded ? 'rgba(244,123,32,0.9)' : 'rgba(0,0,0,0.55)',
                        border: 'none', borderRadius: '4px',
                        color: '#fff', cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        zIndex: 3,
                      }}
                    >
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        {isExpanded
                          ? <polyline points="18 15 12 9 6 15"/>
                          : <><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></>
                        }
                      </svg>
                    </button>
                  )}

                  {/* Normal bottom overlay: name + Go */}
                  {!isExpanded && (
                    <div style={{
                      position: 'absolute', bottom: 0, left: 0, right: 0,
                      background: 'linear-gradient(to top, rgba(0,0,0,0.82) 0%, rgba(0,0,0,0.45) 60%, transparent 100%)',
                      padding: '16px 8px 6px',
                      display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: '4px',
                      zIndex: 2,
                    }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontFamily: 'var(--font-display)', fontSize: '11px', fontWeight: 700, color: isActive ? 'var(--accent-green)' : '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textShadow: '0 1px 3px rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                          {isActive && <span style={{ fontSize: '7px', flexShrink: 0 }}>●</span>}
                          {preset.displayName || preset.name}
                        </div>
                        <div style={{ fontFamily: 'var(--font-mono)', fontSize: '9px', color: preset.source === 'axisvms' ? 'var(--accent-cyan)' : 'rgba(255,255,255,0.4)', marginTop: '1px' }}>
                          {preset.source === 'axisvms' ? '● local' : `#${preset.cameraPresetId ?? preset.id}`}
                        </div>
                      </div>
                      <button
                        className="btn btn-ghost btn-sm"
                        style={{ padding: '2px 9px', fontSize: '10px', flexShrink: 0, background: 'rgba(255,255,255,0.12)', borderColor: 'rgba(255,255,255,0.25)', color: '#fff' }}
                        onClick={e => { e.stopPropagation(); gotoPreset(preset); }}
                      >Go</button>
                    </div>
                  )}

                  {/* Edit overlay */}
                  {isExpanded && (
                    <div style={{
                      position: 'absolute', inset: 0,
                      background: 'rgba(0,0,0,0.82)',
                      display: 'flex', flexDirection: 'column', justifyContent: 'flex-end',
                      padding: '6px 8px 8px',
                      gap: '5px',
                      zIndex: 2,
                    }}>
                      {isEditingName ? (
                        <input
                          autoFocus
                          className="input-field"
                          style={{ height: '26px', fontSize: '11px', padding: '2px 6px', width: '100%', boxSizing: 'border-box' }}
                          value={editingName}
                          onChange={e => setEditingName(e.target.value)}
                          onKeyDown={e => {
                            if (e.key === 'Enter') saveEditName(preset);
                            if (e.key === 'Escape') setEditingPresetId(null);
                          }}
                          onBlur={() => saveEditName(preset)}
                        />
                      ) : (
                        <div
                          onClick={() => { setEditingPresetId(preset.id); setEditingName(preset.displayName || preset.name); }}
                          title="Click to rename"
                          style={{ fontFamily: 'var(--font-display)', fontSize: '11px', fontWeight: 700, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'text', padding: '2px 4px', borderRadius: '3px', border: '1px dashed rgba(255,255,255,0.25)' }}
                        >
                          {preset.displayName || preset.name}
                        </div>
                      )}
                      <div style={{ display: 'flex', gap: '4px' }}>
                        <button
                          className="btn btn-ghost btn-sm"
                          style={{ flex: 1, justifyContent: 'center', padding: '3px 2px', fontSize: '10px', background: 'rgba(255,255,255,0.1)', borderColor: 'rgba(255,255,255,0.2)', color: '#fff' }}
                          onClick={e => { e.stopPropagation(); gotoPreset(preset); }}
                        >Go</button>
                        <button
                          className="btn btn-ghost btn-sm"
                          style={{ flex: 1, justifyContent: 'center', padding: '3px 2px', fontSize: '10px', background: 'rgba(255,255,255,0.1)', borderColor: 'rgba(255,255,255,0.2)', color: '#fff' }}
                          onClick={e => { e.stopPropagation(); updatePosition(preset); }}
                          disabled={updatingPos === preset.id}
                          title="Save current PTZ position"
                        >
                          {updatingPos === preset.id ? '...' : (
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z"/>
                              <polyline points="17 21 17 13 7 13 7 21"/>
                              <polyline points="7 3 7 8 15 8"/>
                            </svg>
                          )}
                        </button>
                        <button
                          className="btn btn-ghost btn-sm"
                          style={{ flex: 1, justifyContent: 'center', padding: '3px 2px', fontSize: '10px', background: 'rgba(255,255,255,0.1)', borderColor: 'rgba(255,255,255,0.2)', color: '#fff' }}
                          onClick={e => { e.stopPropagation(); captureThumb(preset); }}
                          disabled={capturingThumb === preset.id}
                          title="Capture thumbnail"
                        >
                          {capturingThumb === preset.id ? '...' : (
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z"/><circle cx="12" cy="13" r="4"/></svg>
                          )}
                        </button>
                        <button
                          className="btn btn-ghost btn-sm"
                          style={{ flex: 1, justifyContent: 'center', padding: '3px 2px', fontSize: '10px', background: 'rgba(255,51,102,0.18)', borderColor: 'rgba(255,51,102,0.45)', color: 'var(--accent-red)' }}
                          onClick={e => { e.stopPropagation(); deletePreset(preset); }}
                          title="Delete preset"
                        >
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showSyncModal && (
        <SyncPresetsModal
          camera={camera}
          localPresets={presets.filter(p => p.source === 'axisvms')}
          onClose={() => setShowSyncModal(false)}
          onSynced={() => { setShowSyncModal(false); loadPresets(); }}
        />
      )}
    </div>
  );
}
